import { useState } from 'react';
import WebApp from '@twa-dev/sdk';
import { messages } from '@app/shared';
import { useT } from '../i18n';
import { haptic } from '../lib/tma';
import { voiceApi, VoiceApiError } from '../lib/voice-client';
import { Button, Card, Toast } from './ui';

/** The bot's chat; outside Telegram — a new tab. */
function openChat(url: string): void {
  try {
    WebApp.openTelegramLink(url);
  } catch {
    window.open(url, '_blank', 'noopener');
  }
}

/**
 * Spec 6.3: when this phone cannot record in the Mini App, the sample and the
 * consent go as two voices in the bot's chat. The consent box is ticked here,
 * before the bot asks for anything.
 */
export function ChatSampleFallback() {
  const t = useT();
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [botUrl, setBotUrl] = useState<string | null | undefined>(undefined);
  const [failed, setFailed] = useState<string | null>(null);

  /** Ends the wait, so the next voice in the chat is a thought again. */
  async function cancel() {
    setBusy(true);
    try {
      await voiceApi.cancelChatSample();
      setBotUrl(undefined);
    } catch {
      setFailed(t.common.failed);
    } finally {
      setBusy(false);
    }
  }

  async function start() {
    setBusy(true);
    setFailed(null);
    try {
      const res = await voiceApi.startChatSample();
      haptic('success');
      setBotUrl(res.botUrl);
      if (res.botUrl) openChat(res.botUrl);
    } catch (err) {
      setFailed(
        err instanceof VoiceApiError && err.code === 'CONSENT_OUTDATED'
          ? t.voiceSetup.outdated
          : t.common.failed,
      );
    } finally {
      setBusy(false);
    }
  }

  if (botUrl !== undefined) {
    return (
      <Card variant="surface" padding="md" className="space-y-3">
        <p className="text-sm">{t.voiceSetup.chatSent}</p>
        {botUrl && (
          <Button variant="secondary" fullWidth onClick={() => openChat(botUrl)}>
            {t.voiceSetup.openChat}
          </Button>
        )}
        {failed && <Toast tone="destructive">{failed}</Toast>}
        <Button variant="secondary" fullWidth loading={busy} onClick={() => void cancel()}>
          {t.voiceSetup.chatCancel}
        </Button>
      </Card>
    );
  }

  return (
    <Card variant="surface" padding="md" className="space-y-3">
      <h3 className="text-sm font-semibold">{t.voiceSetup.chatTitle}</h3>
      <p className="text-sm">{t.voiceSetup.chatText}</p>
      <p className="text-xs text-hint">«{messages().voiceConsentPhrase}»</p>
      <label className="flex items-start gap-3 text-sm">
        <input
          type="checkbox"
          checked={ticked}
          onChange={(e) => setTicked(e.target.checked)}
          className="mt-0.5 w-5 h-5 flex-shrink-0 accent-[rgb(var(--ink))]"
        />
        <span>{t.voiceSetup.consentBox}</span>
      </label>
      {failed && <Toast tone="destructive">{failed}</Toast>}
      <Button
        variant="ink"
        fullWidth
        disabled={!ticked}
        loading={busy}
        onClick={() => void start()}
      >
        {t.voiceSetup.chatButton}
      </Button>
    </Card>
  );
}
